const KEY = '__cardivue_manual__';

type ManualState = {
  mode: boolean;
  dirty: boolean;
  canApplyModification: boolean;
  events: EventTarget;
};

function store(): ManualState {
  const g = globalThis as any;

  if (!g[KEY]) {
    g[KEY] = {
      mode: false,
      dirty: false,
      canApplyModification: false,
      events: new EventTarget(),
    } satisfies ManualState;
  }

  return g[KEY] as ManualState;
}

function emit(name: string, value: boolean): void {
  store().events.dispatchEvent(
    new CustomEvent(name, {
      detail: value,
    }),
  );
}

function on(name: string, cb: (value: boolean) => void): () => void {
  const events = store().events;

  const listener = (ev: Event) => {
    cb((ev as CustomEvent<boolean>).detail);
  };

  events.addEventListener(name, listener);

  return () => {
    events.removeEventListener(name, listener);
  };
}

export function setManualMode(mode: boolean): void {
  const api = window.electron?.manual;

  if (api) {
    api.setMode(mode);
    return;
  }

  const value = !!mode;
  store().mode = value;
  emit('mode', value);
}

export async function getManualMode(): Promise<boolean> {
  const api = window.electron?.manual;

  if (api) {
    return api.getMode();
  }

  return store().mode;
}

export function onManualModeChanged(cb: (mode: boolean) => void): () => void {
  const api = window.electron?.manual;

  if (api?.onModeChanged) {
    const unsubscribe = api.onModeChanged(cb);

    return typeof unsubscribe === 'function' ? unsubscribe : () => {};
  }

  return on('mode', cb);
}

export function setManualDirty(dirty: boolean): void {
  const api = window.electron?.manual;

  if (api) {
    api.setDirty(dirty);
    return;
  }

  const value = !!dirty;
  store().dirty = value;
  emit('dirty', value);
}

export async function getManualDirty(): Promise<boolean> {
  const api = window.electron?.manual;

  if (api) {
    return api.getDirty();
  }

  return store().dirty;
}

export async function clearManualDirty(): Promise<boolean> {
  const api = window.electron?.manual;

  if (api) {
    return api.clearDirty();
  }

  store().dirty = false;
  emit('dirty', false);
  return true;
}

export function onManualDirtyChanged(cb: (dirty: boolean) => void): () => void {
  const api = window.electron?.manual;

  if (api?.onDirtyChanged) {
    const unsubscribe = api.onDirtyChanged(cb);
    return typeof unsubscribe === 'function' ? unsubscribe : () => {};
  }

  return on('dirty', cb);
}

export function setManualCanApplyModification(value: boolean): void {
  const nextValue = !!value;
  if (store().canApplyModification === nextValue) return;

  store().canApplyModification = nextValue;
  emit('canApplyModification', nextValue);
}

export function getManualCanApplyModification(): boolean {
  return store().canApplyModification;
}

export function onManualCanApplyModificationChanged(
  cb: (value: boolean) => void,
): () => void {
  return on('canApplyModification', cb);
}
